<template>
  <div class="app-provider">
    <app-content v-if="loaded" :key="reloadKey" />
    <app-toast v-if="toastMessage" :message="toastMessage" />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { toastCaptureMixin } from '@rready/design';
import type { IWorkflowV2 } from '@rready/sdk';
import { overrideTranslations, updateLocale } from '@rready/localization';
import AppContent from './AppContent.vue';
import { useAuthStore } from './store/authStore';
import { useContextStore } from './store/contextStore';
import { useProductStore } from './store/productStore';
import { resolveProduct } from './router/navigation/resolver';
import { useApplicationScopeStore } from './store/applicationScopeStore';

export default defineComponent({
  components: {
    AppContent
  },
  mixins: [toastCaptureMixin],
  data() {
    return {
      reloadKey: 0,
      loaded: false
    };
  },
  watch: {
    async routeProductId(val, old): Promise<void> {
      if (val === '_') {
        return;
      }
      this.changeDocumentTitle(val, old);
      await this.load();
      this.reloadKey += 1;
    }
  },
  async created() {
    if (this.$route.path === '/') {
      this.$router.replace({
        name: 'Dashboard',
        params: { product: '_' }
      });
    }
    await this.load();
  },
  computed: {
    routeProductId(): string {
      return this.$route.params.product;
    },
    availableLanguages(): string[] {
      return useContextStore().organization.language.available;
    },
    workflow(): IWorkflowV2 {
      return useProductStore().workflow;
    }
  },
  methods: {
    async load(): Promise<void> {
      const current = this.$route.params.product;

      const productId =
        current === '_' ? resolveProduct() : resolveProduct(current);

      if (!productId) {
        this.handleProductSelectionError();
        return;
      }

      if (productId !== this.$route.params.product) {
        const { searchParams, hash } = new URL(window.location.href);
        const query = Object.fromEntries(searchParams.entries());

        this.$router.replace({
          params: { product: productId },
          query,
          hash
        });
      }

      this.loadProduct(productId);

      const language = useAuthStore().user?.language?.toLocaleLowerCase();
      overrideTranslations(this.workflow.product?.resources ?? {});
      updateLocale(language ?? 'en');

      this.loaded = true;
    },
    async loadProduct(productId: string): Promise<void> {
      const product = useApplicationScopeStore().products.find(
        (it) => it.name === productId
      );

      useProductStore().updateProductId(productId);
      useProductStore().updateWorkflow(product);
    },
    changeDocumentTitle(val: string, old: string): void {
      document.title = document.title.replace(
        old?.toUpperCase(),
        val?.toUpperCase()
      );
    },
    handleProductSelectionError() {
      this.loaded = true;
      this.$router.replace({
        name: 'AppError',
        params: {
          errorCode: 'invalid-product'
        },
        query: {
          invalidProductId: this.routeProductId
        }
      });
    }
  }
});
</script>

<style lang="scss" scoped>
.app-toast {
  position: fixed;
}
</style>
